import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useT } from "../../lib/i18n";
import LanguageToggle from "../LanguageToggle";
import { COMPANY } from "../../lib/company";

export const Logo = ({ light = false, size = "md" }) => {
  const h = size === "sm" ? "h-9" : size === "lg" ? "h-16" : "h-12";
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group select-none">
      <img
        src="/brand/ema-logo-transparent.png"
        alt="EMA Kurer ApS"
        className={`${h} w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04] ${light ? "" : "drop-shadow-sm"}`}
        loading="eager"
        decoding="async"
      />
      <div className="leading-none hidden lg:block">
        <div className={`font-display font-bold text-base tracking-tight whitespace-nowrap ${light ? "text-white" : "text-brand-dark"}`}>
          EMA Kurer<span className="text-brand-red">.</span>
        </div>
        <div className={`text-[9px] tracking-[0.28em] uppercase font-semibold mt-1 whitespace-nowrap ${light ? "text-white/55" : "text-brand-text2"}`}>
          Same-day · Sjælland
        </div>
      </div>
    </Link>
  );
};

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const t = useT();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/om-os", label: t("nav.about") },
    { to: "/services", label: t("nav.services") },
    { to: "/erhverv/faa-tilbud", label: "Få tilbud" },
    { to: "/track", label: t("nav.track") },
    { to: "/anmeldelser", label: t("nav.reviews") },
    { to: "/erhverv", label: t("nav.business") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/bliv-chauffoer", label: t("nav.driver") },
    { to: "/vognmand-partner", label: t("nav.subcontractor") },
    { to: "/kontakt", label: t("nav.contact") },
  ];

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-brand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight whitespace-nowrap transition-colors ${isActive ? "text-brand-red" : "text-brand-dark hover:text-brand-red"}`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle className="hidden sm:inline-flex" />
          <a
            href={`tel:${COMPANY.phone_e164}`}
            data-testid="nav-phone"
            className="hidden 2xl:inline-flex items-center gap-1.5 text-xs font-bold tracking-wide text-brand-dark hover:text-brand-red transition px-1 whitespace-nowrap"
            title="Ring til EMA Kurer"
          >
            <Phone size={14} className="text-brand-red shrink-0" />
            {COMPANY.phone}
          </a>
          <Link to="/login" data-testid="nav-cta-login" className="hidden 2xl:inline-flex text-xs font-bold tracking-widest uppercase text-brand-dark hover:text-brand-red transition px-1 whitespace-nowrap">
            Min Side
          </Link>
          <Link to="/book" data-testid="nav-cta-book" className="hidden sm:inline-flex btn-primary text-sm py-2.5 px-4 whitespace-nowrap">
            {t("nav.book")}
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden w-10 h-10 flex items-center justify-center border border-brand text-brand-dark shrink-0"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-brand bg-white" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-base font-semibold py-1 ${isActive ? "text-brand-red" : "text-brand-dark"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-2 flex flex-col gap-3 border-t border-brand">
              <a
                href={`tel:${COMPANY.phone_e164}`}
                data-testid="mobile-nav-phone"
                className="inline-flex items-center gap-2 text-sm font-bold text-brand-dark hover:text-brand-red transition pt-3"
              >
                <Phone size={16} className="text-brand-red" />
                {COMPANY.phone}
              </a>
              <Link to="/login" data-testid="mobile-nav-login" className="text-sm font-bold tracking-widest uppercase text-brand-dark hover:text-brand-red transition">
                Min Side
              </Link>
              <div className="flex items-center justify-between gap-3">
                <LanguageToggle />
                <Link to="/book" data-testid="mobile-nav-cta" className="btn-primary justify-center">{t("nav.book")}</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
